import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Tabs,
  Tab,
  Alert,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFileInvoice,
  FaUserCircle,
  FaIdCard,
  FaHashtag,
  FaMoneyBillWave,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaBoxes,
  FaBook,
  FaFileAlt,
  FaDownload,
  FaEye,
  FaPlus,
  FaClock,
} from "react-icons/fa";
import {
  getCustomerById,
  deleteCustomer,
  getCustomerConnections,
  getCustomerBookings,
  getCustomerOrders,
  getCustomerInvoices,
  downloadInvoice,
} from "../../components/services/customerService";
import {
  countryApi,
  stateApi,
  cityApi,
  pincodeApi,
} from "../../api/superadmin/masterData.api";
import Swal from "sweetalert2";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// =============================================
// IMPORT ALL MODALS
// =============================================
import CreateConnectionModal from "../../components/Modals/CreateConnectionModal";
import CreateBookingModal from "../../components/Modals/CreateBookingModal";
import ViewConnectionModal from "../../components/Modals/ViewConnectionModal";
import ViewBookingModal from "../../components/Modals/ViewBookingModal";
import EditConnectionModal from "../../components/Modals/EditConnectionModal";
import EditBookingModal from "../../components/Modals/EditBookingModal";
import StatusBadge from "../../components/Common/StatusBadge";
import {
  statusConfig,
  bookingStatusConfig,
  orderStatusConfig,
  invoiceStatusConfig,
  customerTypeConfig,
} from "../../config/statusConfig";

// =============================================
// MAIN COMPONENT
// =============================================

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // =============================================
  // STATE
  // =============================================
  
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [connections, setConnections] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [showFullAadhaar, setShowFullAadhaar] = useState(false);
  const [showFullAlternateMobile, setShowFullAlternateMobile] = useState(false);
  
  // =============================================
  // SEPARATE MODAL STATES - ✅ FIXED
  // =============================================
  
  // ADD Modals
  const [showAddConnectionModal, setShowAddConnectionModal] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  
  // VIEW Modals
  const [showViewConnectionModal, setShowViewConnectionModal] = useState(false);
  const [showViewBookingModal, setShowViewBookingModal] = useState(false);
  
  // EDIT Modals
  const [showEditConnectionModal, setShowEditConnectionModal] = useState(false);
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);
  
  // Selected Items
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  
  // Location names
  const [locationNames, setLocationNames] = useState({
    country: "",
    state: "",
    city: "",
    pincode: "",
  });
  const [loadingLocation, setLoadingLocation] = useState(false);

  // =============================================
  // TOAST HELPERS
  // =============================================

  const showCustomToast = (type, message) => {
    const configs = {
      success: { color: "#28a745", border: "2px solid #28a745" },
      error: { color: "#dc3545", border: "2px solid #dc3545" },
      warning: { color: "#ffc107", border: "2px solid #ffc107" },
      info: { color: "#17a2b8", border: "2px solid #17a2b8" },
    };
    const config = configs[type];
    toast[type](message, {
      position: "top-right",
      autoClose: 3000,
      transition: Bounce,
      style: {
        border: config.border,
        background: "transparent",
        color: config.color,
        boxShadow: "none",
        marginTop: "70px",
      },
      icon: false,
    });
  };

  // =============================================
  // VALIDATION HELPERS
  // =============================================

  const validateGST = (gstNumber) => {
    if (!gstNumber) return { isValid: null, message: "Not provided" };
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber) 
      ? { isValid: true, message: "Valid GST Number" }
      : { isValid: false, message: "Invalid GST Number Format" };
  };

  const validatePAN = (panNumber) => {
    if (!panNumber) return { isValid: null, message: "Not provided" };
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(panNumber)
      ? { isValid: true, message: "Valid PAN Number" }
      : { isValid: false, message: "Invalid PAN Number Format" };
  };

  const validateAadhaar = (aadhaarNumber) => {
    if (!aadhaarNumber) return { isValid: null, message: "Not provided" };
    const cleanNumber = aadhaarNumber.toString().replace(/\s/g, "");
    const aadhaarRegex = /^[2-9]{1}[0-9]{11}$/;
    return aadhaarRegex.test(cleanNumber)
      ? { isValid: true, message: "Valid Aadhaar Number" }
      : { isValid: false, message: "Invalid Aadhaar Number (12 digits required)" };
  };

  const validateEmail = (email) => {
    if (!email) return { isValid: null, message: "Not provided" };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email)
      ? { isValid: true, message: "Valid Email" }
      : { isValid: false, message: "Invalid Email Format" };
  };

  const validateMobile = (mobile) => {
    if (!mobile) return { isValid: null, message: "Not provided" };
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile)
      ? { isValid: true, message: "Valid Mobile Number" }
      : { isValid: false, message: "Invalid Mobile Number" };
  };

  const validateAlternateMobile = (alternateMobile, primaryMobile) => {
    if (!alternateMobile) return { isValid: null, message: "Not provided" };
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(alternateMobile)) {
      return { isValid: false, message: "Invalid Mobile Number" };
    }
    if (alternateMobile === primaryMobile) {
      return { isValid: false, message: "Cannot be same as primary mobile" };
    }
    return { isValid: true, message: "Valid Alternate Mobile" };
  };

  const getValidationIcon = (validation) => {
    if (validation.isValid === null) {
      return <FaInfoCircle className="text-secondary ms-2" size={14} title={validation.message} />;
    }
    if (validation.isValid) {
      return <FaCheckCircle className="text-success ms-2" size={14} title={validation.message} />;
    }
    return <FaExclamationTriangle className="text-danger ms-2" size={14} title={validation.message} />;
  };

  // =============================================
  // DATA FETCHING
  // =============================================

  const extractDataFromResponse = (response) => {
    if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
      return response.data.data.data;
    }
    if (response?.data?.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    if (response?.data && Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  };

  const fetchLocationNames = async (customerData) => {
    setLoadingLocation(true);
    try {
      const names = { country: "", state: "", city: "", pincode: "" };

      if (customerData.country_id) {
        try {
          const response = await countryApi.getAll();
          const countries = extractDataFromResponse(response);
          const country = countries.find(c => c.id === parseInt(customerData.country_id));
          names.country = country ? country.name : `ID: ${customerData.country_id}`;
        } catch (error) {
          names.country = `ID: ${customerData.country_id}`;
        }
      }

      if (customerData.state_id) {
        try {
          const response = await stateApi.getAll(customerData.country_id);
          const states = extractDataFromResponse(response);
          const state = states.find(s => s.id === parseInt(customerData.state_id));
          names.state = state ? state.name : `ID: ${customerData.state_id}`;
        } catch (error) {
          names.state = `ID: ${customerData.state_id}`;
        }
      }

      if (customerData.city_id) {
        try {
          const response = await cityApi.getAll();
          const cities = extractDataFromResponse(response);
          const city = cities.find(c => c.id === parseInt(customerData.city_id));
          names.city = city ? city.name : `ID: ${customerData.city_id}`;
        } catch (error) {
          names.city = `ID: ${customerData.city_id}`;
        }
      }

      if (customerData.pincode_id) {
        try {
          const response = await pincodeApi.getAll();
          const pincodes = extractDataFromResponse(response);
          const pincode = pincodes.find(p => p.id === parseInt(customerData.pincode_id));
          names.pincode = pincode ? `${pincode.code}` : `ID: ${customerData.pincode_id}`;
        } catch (error) {
          names.pincode = `ID: ${customerData.pincode_id}`;
        }
      }

      setLocationNames(names);
    } catch (error) {
      console.error("Error fetching location names:", error);
    } finally {
      setLoadingLocation(false);
    }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomerById(id);
      let customerData = null;
      if (data?.data?.success && data.data.data) {
        customerData = data.data.data;
      } else if (data?.data) {
        customerData = data.data;
      } else if (data) {
        customerData = data;
      }
      setCustomer(customerData);

      if (customerData) {
        await fetchLocationNames(customerData);
        
        const [connectionsData, bookingsData, ordersData, invoicesData] = await Promise.all([
          getCustomerConnections(id),
          getCustomerBookings(id),
          getCustomerOrders(id),
          getCustomerInvoices(id),
        ]);

        setConnections(Array.isArray(connectionsData) ? connectionsData : []);
        setBookings(Array.isArray(bookingsData) ? bookingsData : []);
        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      }
    } catch (error) {
      console.error("Failed to fetch customer data:", error);
      showCustomToast("error", "Failed to load customer details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchAllData();
    }
  }, [id, fetchAllData]);

  // =============================================
  // HANDLERS
  // =============================================

  const toggleAadhaarVisibility = () => setShowFullAadhaar(!showFullAadhaar);
  const toggleAlternateMobileVisibility = () => setShowFullAlternateMobile(!showFullAlternateMobile);

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete customer "${customer?.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6c757d",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteCustomer(id);
        showCustomToast("success", `✅ Customer "${customer.name}" deleted successfully!`);
        setTimeout(() => navigate("/customers"), 1500);
      } catch (error) {
        showCustomToast("error", error.response?.data?.message || "Failed to delete customer");
      }
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const blob = await downloadInvoice(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showCustomToast("success", "Invoice downloaded successfully!");
    } catch (error) {
      showCustomToast("error", "Failed to download invoice");
    }
  };

  // =============================================
  // VIEW HANDLERS
  // =============================================

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setShowViewBookingModal(true);
  };

  const handleViewConnection = (connection) => {
    setSelectedConnection(connection);
    setShowViewConnectionModal(true);
  };

  // =============================================
  // EDIT HANDLERS
  // =============================================

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setShowEditBookingModal(true);
  };

  const handleEditConnection = (connection) => {
    setSelectedConnection(connection);
    setShowEditConnectionModal(true);
  };

  // =============================================
  // ADD HANDLERS
  // =============================================

  const handleAddConnection = () => setShowAddConnectionModal(true);
  const handleAddBooking = () => setShowAddBookingModal(true);

  // =============================================
  // FORMAT HELPERS
  // =============================================

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatAadhaar = (aadhaarNumber) => {
    if (!aadhaarNumber) return "N/A";
    const cleanNumber = aadhaarNumber.toString().replace(/[^\d]/g, "");
    if (cleanNumber.length === 12) {
      if (showFullAadhaar) {
        return cleanNumber.replace(/(\d{4})(\d{4})(\d{4})/, "$1 $2 $3");
      }
      return `XXXX XXXX ${cleanNumber.slice(-4)}`;
    }
    return aadhaarNumber;
  };

  const formatAlternateMobile = (alternateMobile) => {
    if (!alternateMobile) return "N/A";
    const cleanNumber = alternateMobile.toString().replace(/[^\d]/g, "");
    if (cleanNumber.length === 10) {
      if (showFullAlternateMobile) {
        return cleanNumber.replace(/(\d{5})(\d{5})/, "$1 $2");
      }
      return `XXXXXX ${cleanNumber.slice(-4)}`;
    }
    return alternateMobile;
  };

  // =============================================
  // VALIDATION OBJECTS
  // =============================================

  const gstValidation = validateGST(customer?.gst_number);
  const panValidation = validatePAN(customer?.pan_number);
  const aadhaarValidation = validateAadhaar(customer?.aadhaar_number);
  const emailValidation = validateEmail(customer?.email);
  const mobileValidation = validateMobile(customer?.mobile);
  const alternateMobileValidation = validateAlternateMobile(customer?.alternate_mobile, customer?.mobile);

  // =============================================
  // RENDER
  // =============================================

  if (loading) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
        <div className="text-center py-5">
          <Spinner animation="border" variant="secondary" size="lg" />
          <h5 className="mt-3 text-muted">Loading customer details...</h5>
        </div>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
        <Alert variant="secondary" className="text-center">
          <h4>Customer not found</h4>
          <p>The customer you're looking for doesn't exist.</p>
          <Button variant="secondary" onClick={() => navigate("/customers")}>
            Back to Customers
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

      {/* ============================================= */}
      {/* HEADER */}
      {/* ============================================= */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1" style={{ color: "#1a1a2e" }}>
            {customer.name}
          </h2>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted" style={{ fontSize: "13px" }}>
              <FaHashtag size={12} className="me-1" /> 
              Code: {customer.customer_code || `CUST_${String(customer.id).padStart(6, "0")}`}
            </span>
            <span>•</span>
            <StatusBadge status={customer.customer_type} config={customerTypeConfig} />
            <StatusBadge status={customer.status} config={statusConfig} />
            {customer.kyc_status === "verified" && (
              <FaCheckCircle className="text-success" size={16} title="KYC Verified" />
            )}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            onClick={() => navigate(`/customers/edit/${customer.id}`)}
            style={{
              backgroundColor: "rgb(30, 58, 111)",
              border: "none",
              borderRadius: "30px",
              padding: "8px 20px",
              fontSize: "13px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaEdit size={14} /> Edit
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            style={{
              borderRadius: "30px",
              padding: "8px 20px",
              fontSize: "13px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaTrash size={14} /> Delete
          </Button>
        </div>
      </div>

      {/* ============================================= */}
      {/* STATS CARDS */}
      {/* ============================================= */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="border-0" style={{ borderRadius: "10px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted mb-0" style={{ fontSize: "11px" }}>Total Connections</small>
                  <h5 className="mb-0 fw-bold" style={{ fontSize: "20px" }}>{connections.length}</h5>
                </div>
                <div style={{ backgroundColor: "#D3EAFF", padding: "8px", borderRadius: "10px" }}>
                  <FaBoxes size={18} style={{ color: "#437EF7" }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0" style={{ borderRadius: "10px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted mb-0" style={{ fontSize: "11px" }}>Total Bookings</small>
                  <h5 className="mb-0 fw-bold" style={{ fontSize: "20px" }}>{bookings.length}</h5>
                </div>
                <div style={{ backgroundColor: "#FEF6D7", padding: "8px", borderRadius: "10px" }}>
                  <FaBook size={18} style={{ color: "#FED229" }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0" style={{ borderRadius: "10px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted mb-0" style={{ fontSize: "11px" }}>Total Orders</small>
                  <h5 className="mb-0 fw-bold" style={{ fontSize: "20px" }}>{orders.length}</h5>
                </div>
                <div style={{ backgroundColor: "#FFE0CB", padding: "8px", borderRadius: "10px" }}>
                  <FaFileAlt size={18} style={{ color: "#FF8532" }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0" style={{ borderRadius: "10px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted mb-0" style={{ fontSize: "11px" }}>Total Invoices</small>
                  <h5 className="mb-0 fw-bold" style={{ fontSize: "20px" }}>{invoices.length}</h5>
                </div>
                <div style={{ backgroundColor: "#ECFDF3", padding: "8px", borderRadius: "10px" }}>
                  <FaFileInvoice size={18} style={{ color: "#027A48" }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============================================= */}
      {/* TABS */}
      {/* ============================================= */}
      <Card className="border-0 shadow-sm rounded-3">
        <Card.Header className="bg-white border-0 pt-3 px-4">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="border-0 gap-2"
            style={{ borderBottom: "2px solid #e9ecef" }}
          >
            {/* ============================================= */}
            {/* OVERVIEW TAB */}
            {/* ============================================= */}
            <Tab
              eventKey="overview"
              title={<span className="fw-semibold"><FaUserCircle className="me-2" /> Overview</span>}
              tabClassName="border-0"
            >
              <div className="p-3">
                {/* Personal Information */}
                <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaUser className="me-2" /> Personal Information
                </h6>
                <hr className="mt-0 mb-3" />
                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Customer Name</small>
                      <strong>{customer.name}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Customer Code</small>
                      <strong>{customer.customer_code || `CUST_${String(customer.id).padStart(6, "0")}`}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Customer Type</small>
                      <StatusBadge status={customer.customer_type} config={customerTypeConfig} />
                    </div>
                  </Col>
                </Row>

                {/* Contact Information */}
                <h6 className="fw-bold mb-3 mt-4" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaPhone className="me-2" /> Contact Information
                </h6>
                <hr className="mt-0 mb-3" />
                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Email Address</small>
                      <div className="d-flex align-items-center">
                        <strong>{customer.email || "N/A"}</strong>
                        {getValidationIcon(emailValidation)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Mobile Number</small>
                      <div className="d-flex align-items-center">
                        <strong>{customer.mobile || "N/A"}</strong>
                        {getValidationIcon(mobileValidation)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Alternate Mobile</small>
                      <div className="d-flex align-items-center">
                        <span style={{ cursor: "pointer" }} onClick={toggleAlternateMobileVisibility} className="me-1">
                          <strong>{formatAlternateMobile(customer.alternate_mobile)}</strong>
                        </span>
                        {customer.alternate_mobile && (
                          <Button variant="link" size="sm" onClick={toggleAlternateMobileVisibility} className="p-0 ms-1">
                            {showFullAlternateMobile ? "Hide" : "Show"}
                          </Button>
                        )}
                        {getValidationIcon(alternateMobileValidation)}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Tax Information */}
                <h6 className="fw-bold mb-3 mt-4" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaIdCard className="me-2" /> Tax Information
                </h6>
                <hr className="mt-0 mb-3" />
                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>GST Number</small>
                      <div className="d-flex align-items-center">
                        <strong>{customer.gst_number || "N/A"}</strong>
                        {getValidationIcon(gstValidation)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>PAN Number</small>
                      <div className="d-flex align-items-center">
                        <strong>{customer.pan_number || "N/A"}</strong>
                        {getValidationIcon(panValidation)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Aadhaar Number</small>
                      <div className="d-flex align-items-center">
                        <span style={{ cursor: "pointer" }} onClick={toggleAadhaarVisibility} className="me-1">
                          <strong>{formatAadhaar(customer.aadhaar_number)}</strong>
                        </span>
                        {customer.aadhaar_number && (
                          <Button variant="link" size="sm" onClick={toggleAadhaarVisibility} className="p-0 ms-1">
                            {showFullAadhaar ? "Hide" : "Show"}
                          </Button>
                        )}
                        {getValidationIcon(aadhaarValidation)}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Address Information */}
                <h6 className="fw-bold mb-3 mt-4" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaMapMarkerAlt className="me-2" /> Address Information
                </h6>
                <hr className="mt-0 mb-3" />
                <Row className="g-3">
                  <Col md={6}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Full Address</small>
                      <strong>{customer.address || "N/A"}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Landmark</small>
                      <strong>{customer.landmark || "N/A"}</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Country</small>
                      <strong>{loadingLocation ? <Spinner animation="border" size="sm" /> : locationNames.country || "N/A"}</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>State</small>
                      <strong>{loadingLocation ? <Spinner animation="border" size="sm" /> : locationNames.state || "N/A"}</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>City</small>
                      <strong>{loadingLocation ? <Spinner animation="border" size="sm" /> : locationNames.city || "N/A"}</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Pincode</small>
                      <strong>{loadingLocation ? <Spinner animation="border" size="sm" /> : locationNames.pincode || "N/A"}</strong>
                    </div>
                  </Col>
                </Row>

                {/* Financial Information */}
                <h6 className="fw-bold mb-3 mt-4" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaMoneyBillWave className="me-2" /> Financial Information
                </h6>
                <hr className="mt-0 mb-3" />
                <Row className="g-3">
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Credit Limit</small>
                      <strong>{formatCurrency(customer.credit_limit || 0)}</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Credit Days</small>
                      <strong>{customer.credit_days || 0} days</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Outstanding Amount</small>
                      <strong>{formatCurrency(customer.outstanding_amount || 0)}</strong>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: "11px" }}>Created On</small>
                      <strong>{formatDate(customer.created_at)}</strong>
                    </div>
                  </Col>
                </Row>
              </div>
            </Tab>

            {/* ============================================= */}
            {/* CONNECTIONS TAB */}
            {/* ============================================= */}
            <Tab
              eventKey="connections"
              title={<span className="fw-semibold"><FaBoxes className="me-2" /> Connections ({connections.length})</span>}
              tabClassName="border-0"
            >
              <div className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
                    <FaBoxes className="me-2" /> Connection Details
                  </h6>
                  <Button size="sm" onClick={handleAddConnection} style={{ backgroundColor: "rgb(30, 58, 111)", border: "none", borderRadius: "20px", padding: "6px 16px", fontSize: "12px" }}>
                    <FaPlus className="me-1" /> Add Connection
                  </Button>
                </div>
                {connections.length === 0 ? (
                  <div className="text-center py-5">
                    <FaBoxes size={40} className="text-muted mb-3" />
                    <h5 className="text-muted">No connections found</h5>
                    <p className="text-muted small">This customer doesn't have any active connections</p>
                    <Button variant="primary" size="sm" onClick={handleAddConnection}>
                      <FaPlus className="me-1" /> Add Connection
                    </Button>
                  </div>
                ) : (
                  <Table responsive hover className="mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>Connection #</th>
                        <th>Type</th>
                        <th>Capacity</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.map((conn) => (
                        <tr key={conn.id}>
                          <td><strong>{conn.connection_number}</strong></td>
                          <td>{conn.gas_type?.toUpperCase() || "LPG"}</td>
                          <td>{conn.cylinder_capacity}</td>
                          <td>{formatDate(conn.connection_date)}</td>
                          <td><StatusBadge status={conn.connection_status} config={statusConfig} /></td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button variant="outline-primary" size="sm" onClick={() => handleViewConnection(conn)} title="View">
                                <FaEye size={12} />
                              </Button>
                              <Button variant="outline-warning" size="sm" onClick={() => handleEditConnection(conn)} title="Edit">
                                <FaEdit size={12} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </Tab>

            {/* ============================================= */}
            {/* BOOKINGS TAB */}
            {/* ============================================= */}
            <Tab
              eventKey="bookings"
              title={<span className="fw-semibold"><FaBook className="me-2" /> Bookings ({bookings.length})</span>}
              tabClassName="border-0"
            >
              <div className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
                    <FaBook className="me-2" /> Booking History
                  </h6>
                  <Button size="sm" onClick={handleAddBooking} style={{ backgroundColor: "rgb(30, 58, 111)", border: "none", borderRadius: "20px", padding: "6px 16px", fontSize: "12px" }}>
                    <FaPlus className="me-1" /> New Booking
                  </Button>
                </div>
                {bookings.length === 0 ? (
                  <div className="text-center py-5">
                    <FaBook size={40} className="text-muted mb-3" />
                    <h5 className="text-muted">No bookings found</h5>
                    <p className="text-muted small">This customer hasn't made any bookings yet</p>
                    <Button variant="primary" size="sm" onClick={handleAddBooking}>
                      <FaPlus className="me-1" /> New Booking
                    </Button>
                  </div>
                ) : (
                  <Table responsive hover className="mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>Booking #</th>
                        <th>Cylinder</th>
                        <th>Booking Date</th>
                        <th>Delivery Date</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td><strong>{booking.booking_number}</strong></td>
                          <td>{booking.cylinder_type_name || booking.cylinder_type || 'N/A'}</td>
                          <td>{formatDate(booking.booking_date)}</td>
                          <td>{formatDate(booking.preferred_delivery_date)}</td>
                          <td><StatusBadge status={booking.booking_status} config={bookingStatusConfig} /></td>
                          <td>{formatCurrency(booking.total_amount || 0)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button variant="outline-primary" size="sm" onClick={() => handleViewBooking(booking)} title="View">
                                <FaEye size={12} />
                              </Button>
                              <Button variant="outline-warning" size="sm" onClick={() => handleEditBooking(booking)} title="Edit Status">
                                <FaClock size={12} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </Tab>

            {/* ============================================= */}
            {/* ORDERS TAB */}
            {/* ============================================= */}
            <Tab
              eventKey="orders"
              title={<span className="fw-semibold"><FaFileAlt className="me-2" /> Orders ({orders.length})</span>}
              tabClassName="border-0"
            >
              <div className="p-3">
                <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaFileAlt className="me-2" /> Order History
                </h6>
                {orders.length === 0 ? (
                  <div className="text-center py-5">
                    <FaFileAlt size={40} className="text-muted mb-3" />
                    <h5 className="text-muted">No orders found</h5>
                    <p className="text-muted small">This customer doesn't have any orders</p>
                  </div>
                ) : (
                  <Table responsive hover className="mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>Order #</th>
                        <th>Type</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Total</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td><strong>{order.order_no}</strong></td>
                          <td>{order.order_type}</td>
                          <td>{formatDate(order.order_date)}</td>
                          <td><StatusBadge status={order.status} config={orderStatusConfig} /></td>
                          <td><StatusBadge status={order.payment_status} config={invoiceStatusConfig} /></td>
                          <td>{formatCurrency(order.total_amount || 0)}</td>
                          <td>
                            <Button variant="outline-primary" size="sm" onClick={() => navigate(`/orders/${order.id}`)}>
                              <FaEye size={12} /> View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </Tab>

            {/* ============================================= */}
            {/* INVOICES TAB */}
            {/* ============================================= */}
            <Tab
              eventKey="invoices"
              title={<span className="fw-semibold"><FaFileInvoice className="me-2" /> Invoices ({invoices.length})</span>}
              tabClassName="border-0"
            >
              <div className="p-3">
                <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)" }}>
                  <FaFileInvoice className="me-2" /> Invoice History
                </h6>
                {invoices.length === 0 ? (
                  <div className="text-center py-5">
                    <FaFileInvoice size={40} className="text-muted mb-3" />
                    <h5 className="text-muted">No invoices found</h5>
                    <p className="text-muted small">This customer doesn't have any invoices</p>
                  </div>
                ) : (
                  <Table responsive hover className="mb-0" style={{ fontSize: "14px" }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th>Due Date</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td><strong>{invoice.invoice_no}</strong></td>
                          <td>{formatDate(invoice.invoice_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td><StatusBadge status={invoice.payment_status} config={invoiceStatusConfig} /></td>
                          <td>{formatCurrency(invoice.net_amount || 0)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button variant="outline-primary" size="sm" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                                <FaEye size={12} /> View
                              </Button>
                              <Button variant="outline-success" size="sm" onClick={() => handleDownloadInvoice(invoice.id)}>
                                <FaDownload size={12} /> Download
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card.Header>
      </Card>

      {/* ============================================= */}
      {/* MODALS */}
      {/* ============================================= */}

      {/* VIEW MODALS */}
      <ViewConnectionModal
        show={showViewConnectionModal}
        onHide={() => setShowViewConnectionModal(false)}
        connection={selectedConnection}
      />

      <ViewBookingModal
        show={showViewBookingModal}
        onHide={() => setShowViewBookingModal(false)}
        booking={selectedBooking}
      />

      {/* EDIT MODALS */}
      <EditConnectionModal
        show={showEditConnectionModal}
        onHide={() => setShowEditConnectionModal(false)}
        connection={selectedConnection}
        onSuccess={handleRefresh}
      />

      <EditBookingModal
        show={showEditBookingModal}
        onHide={() => setShowEditBookingModal(false)}
        booking={selectedBooking}
        onSuccess={handleRefresh}
      />

      {/* ADD MODALS */}
      <CreateConnectionModal
        show={showAddConnectionModal}
        onHide={() => setShowAddConnectionModal(false)}
        customerId={id}
        onSuccess={handleRefresh}
      />

      <CreateBookingModal
        show={showAddBookingModal}
        onHide={() => setShowAddBookingModal(false)}
        customerId={id}
        onSuccess={handleRefresh}
      />

      {/* ============================================= */}
      {/* STYLES */}
      {/* ============================================= */}
      <style>{`
        .nav-tabs { border-bottom: none !important; }
        .nav-tabs .nav-link { border: none; color: #6c757d; padding: 10px 16px; font-size: 14px; transition: all 0.2s; border-radius: 30px; margin-right: 8px; }
        .nav-tabs .nav-link:hover { color: rgb(30, 58, 111); background: #f1f5f9; }
        .nav-tabs .nav-link.active { color: rgb(30, 58, 111); background: #eef2ff; border: none; }
        .rounded-3 { border-radius: 12px !important; }
        .table th { font-weight: 600; color: #1e293b; }
        .table td { vertical-align: middle; }
      `}</style>
    </Container>
  );
};

export default CustomerDetails;